import React, { useState, useEffect, useRef } from 'react';
import { FileText, MapPin, Phone, ShoppingCart, Menu as MenuIcon, X } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useLanguage } from '../context/LanguageContext';
import { Language } from '../translations/types';
import { motion } from 'framer-motion';

const Header = () => {
  const { toggleCart, itemCount } = useCart();
  const { language, setLanguage, t } = useLanguage();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showLanguages, setShowLanguages] = useState(false);
  const headerRef = useRef<HTMLElement>(null);

  const languages = [
    { code: 'fr', flag: 'FR', label: 'Français', name: 'FR' },
    { code: 'ar', flag: 'MA', label: 'العربية', name: 'AR' },
    { code: 'en', flag: 'US', label: 'English', name: 'EN' }
  ];

  // Language selector component
  const LanguageSelector = ({ isMobile = false }) => (
    <div 
      data-language-menu
      className={`${
        isMobile 
          ? "fixed top-16 right-4 bg-secondary w-48 rounded-lg shadow-lg z-[100]" 
          : "absolute right-0 mt-2 py-2 w-48 bg-white rounded-xl shadow-lg ring-1 ring-black ring-opacity-5 z-50"
      }`}
      onClick={(e) => e.stopPropagation()}
    >
      {languages.map((lang) => (
        <button
          key={lang.code}
          type="button"
          onClick={() => {
            setLanguage(lang.code as Language);
            setShowLanguages(false);
          }}
          className={`w-full flex items-center justify-between px-4 py-2 ${
            isMobile 
              ? "text-white hover:bg-secondary-light" 
              : "text-gray-700 hover:bg-gray-100"
          }`}
        >
          <div className="flex items-center gap-2">
            <img
              src={`https://flagcdn.com/24x18/${lang.flag.toLowerCase()}.png`}
              alt={`${lang.label} flag`}
              className="w-6 h-4 object-cover rounded"
            />
            <span>{lang.label}</span>
          </div>
          <span className="text-sm opacity-75">{lang.name}</span>
        </button>
      ))}
    </div>
  );

  // Desktop language button
  const DesktopLanguageButton = () => (
    <div className="relative hidden lg:block">
      <button 
        type="button"
        data-language-menu
        onClick={() => setShowLanguages(!showLanguages)}
        aria-haspopup="menu"
        aria-expanded={showLanguages}
        title="Changer de langue"
        className="flex items-center gap-2 px-4 py-2 rounded-lg hover:bg-secondary-light text-white transition-all"
      >
        <img
          src={`https://flagcdn.com/24x18/${languages.find(l => l.code === language)?.flag.toLowerCase()}.png`}
          alt={`${language} flag`}
          className="w-6 h-4 object-cover rounded"
        />
        <span className="text-sm">
          {languages.find(l => l.code === language)?.name}
        </span>
      </button>
      {showLanguages && <LanguageSelector isMobile={false} />}
    </div>
  );

  // Mobile language button
  const MobileLanguageButton = () => (
    <div className="relative lg:hidden">
      <button
        type="button"
        data-language-menu
        onClick={() => setShowLanguages(!showLanguages)}
        aria-haspopup="menu"
        aria-expanded={showLanguages}
        aria-label="Changer de langue"
        className="p-2 rounded-lg text-white hover:bg-secondary-light flex items-center gap-2"
      >
        <img
          src={`https://flagcdn.com/24x18/${languages.find(l => l.code === language)?.flag.toLowerCase()}.png`}
          alt={`${language} flag`}
          className="w-6 h-4 object-cover rounded"
        />
      </button>
      {showLanguages && <LanguageSelector isMobile={true} />}
    </div>
  );

  // Click outside handler. The old `languageMenuRef` was never attached to
  // any element (always null), so this handler was a no-op and the menu
  // never closed when clicking elsewhere. Elements marked with
  // [data-language-menu] (the toggle buttons and the popup itself) count
  // as "inside" so mousedown on the toggle doesn't race its own onClick.
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target;
      if (target instanceof Element && !target.closest('[data-language-menu]')) {
        setShowLanguages(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (!headerRef.current) return;

      const footer = document.querySelector('footer');
      if (!footer) return;

      const footerTop = footer.getBoundingClientRect().top;
      const headerHeight = headerRef.current.offsetHeight;

      if (footerTop <= headerHeight) {
        headerRef.current.style.position = 'absolute';
        headerRef.current.style.top = `${footerTop - headerHeight}px`;
      } else {
        headerRef.current.style.position = 'fixed';
        headerRef.current.style.top = '0';
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const mapsUrl = "https://maps.app.goo.gl/WCYAVc7mXX32VhuS7";
  const appleMapsUrl = "https://maps.apple.com/place?q=Snack+DJOCO&ll=30.927888,-6.900340&t=h";

  const handleCallClick = () => {
    // Use window.location.href directly
    window.location.href = `tel:+212632302829`;
  };

  const handleMapClick = () => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    if (isIOS) {
      window.location.href = appleMapsUrl;
    } else {
      window.open(mapsUrl, '_blank');
    }
  };

  const handleMenuPDFClick = () => {
    window.open(`${process.env.PUBLIC_URL}/menu.pdf`, '_blank');
  };

  return (
    <header ref={headerRef} className="fixed top-0 left-0 right-0 bg-secondary z-50">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Simplified Logo */}
          <div className="flex items-center">
            <img
              src={`${process.env.PUBLIC_URL}/locales/img/djoco.png`}
              alt="DJOCO Logo"
              className="h-12 w-auto object-contain transition-transform hover:scale-105"
              style={{ 
                maxWidth: '100px',
                minWidth: '80px'
              }}
            />
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-4">
            <button
              onClick={handleMenuPDFClick}
              className="flex items-center gap-2 px-4 py-2 rounded-full hover:bg-secondary-light text-white hover:text-primary transition-all font-body"
            >
              <FileText size={20} />
              <span>{t('common.menuPDF')}</span>
            </button>
            
            <button
              onClick={handleMapClick}
              className="flex items-center gap-2 px-4 py-2 rounded-full hover:bg-secondary-light text-white hover:text-primary transition-all font-body"
            >
              <MapPin size={20} />
              <span>{t('common.location')}</span>
            </button>
            
            <button
              onClick={handleCallClick}
              className="flex items-center gap-2 px-4 py-2 rounded-full hover:bg-secondary-light text-white hover:text-primary transition-all font-body"
            >
              <Phone size={20} />
              <span>{t('common.call')}</span>
            </button>

            <DesktopLanguageButton />

            <button
              onClick={toggleCart}
              className="relative flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-[#F59E0B] to-[#F97316] text-white hover:opacity-95 shadow-md transition-all font-body"
            >
              <ShoppingCart className="w-6 h-6" />
              <span>{t('common.cart')}</span>
              {itemCount > 0 && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="absolute -top-1 -right-1 bg-green-500 text-white w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold"
                >
                  {itemCount}
                </motion.div>
              )}
            </button>
          </nav>

          {/* Mobile Navigation */}
          <div className="lg:hidden flex items-center gap-2">
            <MobileLanguageButton />
            
            <button
              type="button"
              onClick={toggleCart}
              aria-label={`${t('common.cart')}${itemCount > 0 ? ` (${itemCount})` : ''}`}
              className="p-2 rounded-lg text-white hover:bg-secondary-light relative"
            >
              <ShoppingCart className="w-6 h-6" aria-hidden="true" />
              {itemCount > 0 && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  aria-hidden="true"
                  className="absolute -top-1 -right-1 bg-green-500 text-white w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold"
                >
                  {itemCount}
                </motion.div>
              )}
            </button>

            <button
              type="button"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label={isMobileMenuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
              aria-expanded={isMobileMenuOpen}
              className="p-2 rounded-lg text-white hover:bg-secondary-light"
            >
              {isMobileMenuOpen ? <X size={24} /> : <MenuIcon size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="lg:hidden border-t border-secondary-light"
          >
            {/* Other mobile menu items */}
            <div className="py-4 space-y-2">
              <button
                onClick={handleMenuPDFClick}
                className="w-full flex items-center gap-2 px-4 py-2 text-white hover:bg-secondary-light rounded-lg"
              >
                <FileText size={20} />
                <span>{t('common.menuPDF')}</span>
              </button>
              
              <button
                onClick={handleMapClick}
                className="w-full flex items-center gap-2 px-4 py-2 text-white hover:bg-secondary-light rounded-lg"
              >
                <MapPin size={20} />
                <span>{t('common.location')}</span>
              </button>
              
              <button
                onClick={handleCallClick}
                className="w-full flex items-center gap-2 px-4 py-2 text-white hover:bg-secondary-light rounded-lg"
              >
                <Phone size={20} />
                <span>{t('common.call')}</span>
              </button>
            </div>
          </motion.div>
        )}
      </div>
    </header>
  );
};

export default Header;